/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { FamilyMemberInput } from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import { Text, EditorialLayout, isValidDate } from '@babylon/medkit'

import { GenericModal, FormRenderer } from '@/components'
import DEFAULT from '@/config/premadeConfigs'
import { useRegionInfo } from '@/redux/regionInfo/selectors'
import { useActiveStepIndex } from '@/redux/signup/selectors'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { Field } from '@/types/partnerConfig'
import { getAge } from '@/utils'
import messages from './messages'
import { EditFamilyMemberProps } from '@/types'
import appStyles from '@/App.module.scss'

const adultFormFields: Field[] = [
  {
    ...DEFAULT.FIELDS.EMAIL,
    labelKey: 'familyPageEmailLabel',
  },
]

interface Props {
  onSubmit: (payload: FamilyMemberInput, id?: number) => Promise<any>
  member?: EditFamilyMemberProps
  id?: number
}

const AddOrEditFamilyMemberModal = ({ onSubmit, member, id }: Props) => {
  const translate = useFormatMessage()
  const { fields } = useConfig()
  const activeStepIndex = useActiveStepIndex()
  const initialFormFields = fields[activeStepIndex]

  const [dateOfBirth, setDateOfBirth] = useState<string | undefined | null>(
    member?.date_of_birth
  )
  const [isAdult, setIsAdult] = useState(true)
  const [formFields, setFormFields] = useState<Field[]>(initialFormFields)
  const { age_of_consent: ageOfConsent } = useRegionInfo()

  const isEditingUser = !!id

  const initialValues: { [key: string]: string } = {}
  const initialFormValues = [...initialFormFields, ...adultFormFields].reduce(
    (acc, { name }) => {
      acc[name] = ''

      return acc
    },
    initialValues
  )

  const onValuesChange = (
    previousValues?: any,
    values?: any,
    changedKeys?: string[]
  ) => {
    const dobKey = DEFAULT.FIELDS.DATE_OF_BIRTH.name

    if (changedKeys?.includes(dobKey)) {
      const dob = values[dobKey]

      if (isValidDate(dob)) {
        setDateOfBirth(dob)
      }
    }
  }

  useEffect(() => {
    if (dateOfBirth && ageOfConsent) {
      const age = getAge(dateOfBirth)

      setIsAdult(age >= ageOfConsent!)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOfBirth])

  useEffect(() => {
    if (isAdult) {
      setFormFields([...initialFormFields, ...adultFormFields])
    } else {
      setFormFields([...initialFormFields])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdult])

  const onSubmitForm = (props: FamilyMemberInput) =>
    isEditingUser ? onSubmit(props, id) : onSubmit(props)

  return (
    <GenericModal
      title={
        isEditingUser
          ? translate(messages.editMemberpageTitle)
          : translate(messages.addMemberpageTitle)
      }
    >
      <EditorialLayout>
        <Text variant="body">{translate(messages.content)}</Text>
        <FormRenderer
          fields={formFields}
          initialValues={{ ...initialFormValues, ...member }}
          submitButtonText={translate(messages.save)}
          testId="add-family-form"
          onSubmit={onSubmitForm}
          onValuesChange={onValuesChange}
        >
          <span className={appStyles.formCtaSpace} />
        </FormRenderer>
      </EditorialLayout>
    </GenericModal>
  )
}

export default AddOrEditFamilyMemberModal
